import React from 'react'

import {
  Body2,
  Box,
  Color,
  Display,
  DisplayPosition,
  FlexDirection,
  Heading3,
  Image,
  JustifyContent,
  Overflow,
  Strong,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'

import { WhatsNewBodySegment } from '../../../config'

const CoverImage = withCitrusTheme(Image, () => ({
  objectFit: 'cover',
  width: '100%',
  height: '100%',
  display: 'block',
}))

// <picture> wrapper so the card image serves WebP with a JPEG fallback (matching the
// HowItWorksSection pattern); fills the image column so the cover image tracks the card height.
const CoverPicture = withCitrusTheme('picture', () => ({
  display: 'block',
  width: '100%',
  height: '100%',
}))

// The design's 8px card radius is outside Citrus's RadiusScale (max 1.5 → 3px),
// so it is applied via withCitrusTheme rather than the borderRadius prop. `flex: 1 1 auto` lets the
// card fill its equal-height grid cell (all cards match the tallest — see CWCWhatsNewSection's grid).
const CardSurface = withCitrusTheme(Box, () => ({
  borderRadius: '8px',
  flex: '1 1 auto',
}))

// Exact Figma typography — the design sizes/line-heights and the `#1A2026` ink
// colour aren't expressible via Citrus's size scale / Color tokens, so they are
// set here. Card heading: Heading 5 (16/24) on mobile, Heading 3 (24/32) from tablet.
const CardHeading = withCitrusTheme(Heading3, (theme) => [
  { color: '#1A2026' },
  theme.responsiveStyle(['16px', null, '24px'], ['fontSize']),
  theme.responsiveStyle(['24px', null, '32px'], ['lineHeight']),
])

// Card body: 14/20 on mobile, 16/24 from tablet; grey `#41505C`.
const CardBody = withCitrusTheme(Body2, (theme) => [
  { color: '#41505C' },
  theme.responsiveStyle(['14px', null, '16px'], ['fontSize']),
  theme.responsiveStyle(['20px', null, '24px'], ['lineHeight']),
])

// Text column fills the space beside the image and shrinks so the copy wraps
// (flex items default to min-width:auto, which would push the card past the viewport).
const TextColumn = withCitrusTheme(Box, () => ({
  flex: '1 1 0',
  minWidth: 0,
}))

export type CWCFeatureCardProps = {
  title: string
  body: WhatsNewBodySegment[]
  /** Card image with a WebP source and a JPEG fallback for older browsers. */
  image: { webp: string; jpg: string }
  /** Optional decorative overlay rendered over the image (e.g. card-3 floating pills). */
  imageOverlay?: React.ReactNode
  /**
   * Optional decorative badge overhanging the top-left corner (e.g. `<CWCNewBadge />`). Off by default;
   * pass a node to render it. Kept as a slot so the NEW badge can be re-enabled in one line.
   */
  badge?: React.ReactNode
}

export const CWCFeatureCard = ({
  title,
  body,
  image,
  imageOverlay,
  badge,
}: CWCFeatureCardProps) => (
  <Box
    position={DisplayPosition.Relative}
    width="100%"
    height="100%"
    display={Display.Flex}
    flexDirection={FlexDirection.Column}
  >
    <CardSurface
      display={Display.Flex}
      flexDirection={FlexDirection.Row}
      bg={Color.White}
      overflowX={Overflow.Hidden}
      overflowY={Overflow.Hidden}
      minHeight={['114px', null, '150px']}
    >
      <Box position={DisplayPosition.Relative} width={['112px', null, '230px']} flexShrink={0}>
        <CoverPicture>
          <source srcSet={image.webp} type="image/webp" />
          <CoverImage src={image.jpg} alt="" />
        </CoverPicture>
        {imageOverlay}
      </Box>
      <TextColumn
        display={Display.Flex}
        flexDirection={FlexDirection.Column}
        justifyContent={JustifyContent.Center}
        paddingH={4}
        paddingV={3}
      >
        <CardHeading>{title}</CardHeading>
        <CardBody>
          {body.map((segment, index) =>
            segment.strong ? (
              // eslint-disable-next-line react/no-array-index-key
              <Strong key={index} color={Color.Primary_600}>
                {segment.text}
              </Strong>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <React.Fragment key={index}>{segment.text}</React.Fragment>
            ),
          )}
        </CardBody>
      </TextColumn>
    </CardSurface>
    {/* Optional decorative badge, painted after the card so it overhangs the top-left corner.
        Offsets account for the sticker's transparent shadow padding (~16px L / 8px T at desktop
        scale) so the visible sticker lands at the Figma "New" frame offset (-24, -15.5 desktop). */}
    {badge && (
      <Box
        position={DisplayPosition.Absolute}
        top={['-24px', null, '-36px']}
        left={['-28px', null, '-52px']}
      >
        {badge}
      </Box>
    )}
  </Box>
)
